import NextArrowIcon from "../../assets/svg/nextArrowIcon";
import createAxiosInstance from "../../utils/axiosConfig";
import { get_top_items } from "../../utils/endpoints";
import { TopicSuggestionProps, TopItemsResponse, TrendingQuestion } from "../../utils/interfaces";
import { useEffect, useState } from "react";

const TopicSuggestionCards = ({ selectUseCase, setSelectSubCase, chatId, handleOptionSelect, startChat }: TopicSuggestionProps) => {
	const [topItems, setTopItems] = useState<TopItemsResponse>();
	const [loading, setLoading] = useState<boolean>(false);
	const [randomQuestions, setRandomQuestions] = useState<number[]>([]);
	const [currentIndex, setCurrentIndex] = useState<number>(0);
	const cardsToShow = 4; // Number of cards to show at once
	
	const getTopItems = async ()=>{
		if (loading) return;
		setLoading(true);
		try{
			const axiosInstance = createAxiosInstance();
			const response = await axiosInstance.get(get_top_items);
			
			// Filter out questions that are too long
			const maxTextLength = "What are the best practices for implementing multi-factor authentication (MFA) in a cloud environment?".length;
			
			const filteredTopics = response.data.topics.map((topic: TrendingQuestion) => ({
				...topic,
				trending_questions: topic.trending_questions?.filter(
					(question: string) => question.length < maxTextLength
				) || []
			})).filter((topic: TrendingQuestion) => (topic.trending_questions?.length || 0) > 0);
			
			setTopItems({
				...response.data,
				topics: filteredTopics
			});
			
			const randomIndices = filteredTopics.slice(0, 5).map(
				(item: TrendingQuestion) => Math.floor(Math.random() * (item?.trending_questions?.length || 0))
			);
			setRandomQuestions(randomIndices);
		} catch (error) {
			console.log(error);
			setLoading(false);
		}finally{
			setLoading(false);
		}
	}

	useEffect(()=>{
		if (!topItems){
			getTopItems();
		}
	}, [topItems]);

	const getRandomUseCase = () => {
		const useCaseValues = [1, 3, 7];
		const randomIndex = Math.floor(Math.random() * useCaseValues.length);
		return useCaseValues[randomIndex];
	};

	const handlePrevious = () => {
		setCurrentIndex((prev) => Math.max(0, prev - 1));
	};

	const handleNext = () => {
		const maxIndex = Math.max(0, (topItems?.topics?.length || 0) - cardsToShow);
		setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
	};

	const canGoPrevious = currentIndex > 0;
	const canGoNext = currentIndex < Math.max(0, (topItems?.topics?.length || 0) - cardsToShow);

	// Calculate the transform value for sliding effect
	const getTransformValue = () => {
		const cardWidth = 100 / cardsToShow; // Each card takes up 25% of container width
		return `translateX(-${currentIndex * cardWidth}%)`;
	};

	const SkeletonCard = () => (
		<div className="col bg-primary">
			<div className="card h-100 d-flex flex-column">
				<div className="card-body d-flex flex-column">
					<div className="d-flex flex-column justify-content-center flex-grow-1">
						<div className="skeleton mb-3" style={{ height: "24px", width: "70%", backgroundColor: "#e0e0e0" }}></div>
					</div>
					<div className="d-flex flex-column justify-content-end flex-grow-1">
						<div className="skeleton" style={{ height: "16px", width: "100%", backgroundColor: "#e0e0e0" }}></div>
						<div className="skeleton mt-2" style={{ height: "16px", width: "80%", backgroundColor: "#e0e0e0" }}></div>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<div className="d-flex align-items-center position-relative">
			{/* Previous Button */}
			<button
				className="btn btn-light rounded-circle me-3 d-flex align-items-center justify-content-center"
				onClick={handlePrevious}
				disabled={!canGoPrevious}
				style={{
					width: "5%",
					height: "40px",
					border: "1px solid #5376F0",
					opacity: canGoPrevious ? 1 : 0.5,
					cursor: canGoPrevious ? "pointer" : "not-allowed",
					backgroundColor: canGoPrevious ? "inherit" : "#f8f9fa",
					padding: "0",
					overflow: "visible",
					fontSize: '1.9rem',
					fontWeight: 'bold'
				}}
			>
				<svg 
					width="16" 
					height="16" 
					fill="currentColor" 
					viewBox="0 0 16 16"
					style={{
						color: canGoPrevious ? "#0C099C" : "#6c757d",
						
					}}
				>
					<path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
				</svg>
			</button>

			{/* Cards Container */}
			<div className="flex-grow-1 overflow-hidden">
				<div className="row g-4">
					{loading ? (
						<>
							<SkeletonCard />
							<SkeletonCard />
							<SkeletonCard />
							<SkeletonCard />
						</>
					) : (
						topItems?.topics?.slice(currentIndex, currentIndex + cardsToShow).map((item: TrendingQuestion, key) => {
							const actualIndex = currentIndex + key;
							const randomQuestion = randomQuestions[actualIndex] || 0;
							return (
								<div className="col" key={`case_cards_${actualIndex}`}>
									<div
										className="card h-100 d-flex flex-column align-items-center justify-content-center"
										onClick={() => {
											selectUseCase(item.label.includes("3") ? Number(item.label.split("_")[2].split(".")[0]) : Number(item.label.split("_")[2]), item?.trending_questions?.[randomQuestion]);
											console.log(item.label.includes("3"), "includes 3");
											if(item.label.includes("3")){
												startChat(Number(item.label.split("_")[2].split(".")[0]), 'b');
											}
										}}
										style={{ 
											cursor: "pointer", 
											color: 'white', 
											borderTopLeftRadius: "30px",
											borderTopRightRadius: "0px",
											borderBottomRightRadius: "30px",
											borderBottomLeftRadius: "30px",
											transition: "transform 0.2s ease-in-out",
											background: "#5376F0",
											fontSize: '0.75rem',
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.transform = "translateY(-2px)";
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.transform = "translateY(0)";
										}}
									>
										<div className="card-body d-flex flex-column">
											<div className="d-flex flex-column justify-content-start flex-grow-1">
												<p className="card-text text-white">
													{item?.trending_questions?.[randomQuestion]}
												</p>
											</div>
										</div>
									</div>
								</div>
							);
						})
					)}
				</div>
			</div>

			{/* Next Button */}
			<button
				className="btn btn-light rounded-circle ms-3 d-flex align-items-center justify-content-center"
				onClick={handleNext}
				disabled={!canGoNext}
				style={{
					width: "5%",
					height: "40px",
					border: "1px solid #5376F0",
					opacity: canGoNext ? 1 : 0.5,
					cursor: canGoNext ? "pointer" : "not-allowed",
					backgroundColor: canGoNext ? "inherit" : "#f8f9fa",
					padding: "0",
					overflow: "visible",
					fontSize: '1.9rem',
					fontWeight: 'bold'
				}}
			>
				<svg 
					width="16" 
					height="16" 
					fill="currentColor" 
					viewBox="0 0 16 16"
					style={{
						color: canGoNext ? "#0C099C" : "#6c757d",
						
					}}
				>
					<path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
				</svg>
			</button>
		</div>
	);
};

export default TopicSuggestionCards;
